/**
 * Чистка событий GlitchTip от того, чего там быть не должно: одноразовых токенов в адресах,
 * куки и авторизации, почтовых адресов, JWT и токенов push.
 *
 * Зачем: с `sendDefaultPii` в событие уходят адрес страницы, Referer и (на сервере) заголовки
 * запроса. У `/reset-password` и `/confirm-registration` в адресе лежит одноразовый токен, у
 * входа через соцсеть - `code`/`state`; увидевший такое событие мог бы войти или сбросить чужой
 * пароль. Событие остаётся целиком, прячется только значение.
 *
 * Правка идёт на месте: события могут быть крупными, а Sentry всё равно отдаёт их нам в
 * `beforeSend` как свои.
 */
export const FILTERED = '[Filtered]';

const SENSITIVE_PARAMS = [
  'token',
  'code',
  'state',
  'challenge',
  'verifier',
  'access_token',
  'refresh_token',
  'id_token',
  'jwt',
  'password',
  'secret',
  'email',
  'key',
  'signature',
].join('|');

/** `?token=…`, `&code=…`, `#access_token=…` и начало строки (query_string приходит без `?`). */
const PARAM_PATTERN = new RegExp(`(^|[?&#;])(${SENSITIVE_PARAMS})=[^&#\\s"']*`, 'gi');
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9-]+(?:\.[A-Z0-9-]+)*\.[A-Z]{2,}/gi;
const JWT_PATTERN = /\beyJ[\w-]{8,}\.[\w-]{8,}\.[\w-]*/g;
/** Токен регистрации FCM: `<id>:APA91b…`. */
const PUSH_TOKEN_PATTERN = /[\w-]{8,}:APA91[\w-]{60,}/g;

const SECRET_HEADERS = new Set(['cookie', 'set-cookie', 'authorization', 'proxy-authorization']);

/** Глубже событие не бывает; предел защищает от самоссылающихся контекстов. */
const MAX_DEPTH = 12;

const scrubString = (value: string): string =>
  value
    .replace(
      PARAM_PATTERN,
      (_match, prefix: string, name: string) => `${prefix}${name}=${FILTERED}`
    )
    .replace(JWT_PATTERN, FILTERED)
    .replace(PUSH_TOKEN_PATTERN, FILTERED)
    .replace(EMAIL_PATTERN, FILTERED);

const scrubValue = (value: unknown, depth: number, seen: WeakSet<object>): unknown => {
  if (typeof value === 'string') {
    return scrubString(value);
  }
  if (typeof value !== 'object' || value === null || depth > MAX_DEPTH || seen.has(value)) {
    return value;
  }
  seen.add(value);

  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    record[key] = SECRET_HEADERS.has(key.toLowerCase())
      ? FILTERED
      : scrubValue(record[key], depth + 1, seen);
  }
  return value;
};

export const scrubTelemetry = <T extends object>(event: T): T => {
  const request = (event as { request?: { cookies?: unknown } }).request;
  if (request) {
    delete request.cookies;
  }
  scrubValue(event, 0, new WeakSet());
  return event;
};
